/*! load-timer.js */
import Log from "app/utils/log";
import ErrorReport from "app/utils/error-report";

const log = new Log("LoadTimer");

const LOAD_TYPES = {
    PLAYER_INIT: "playerinit",
    APP_BUNDLE: "appbundle",
    VENDOR_BUNDLE: "vendorbundle",
    TRITON_ID_SYNC: "tritonidsync",
    TRITON_PROVISION_API: "tritonprovisionapi",
    CSS_FILE: "cssfile",
    IMA_SDK: "imasdk",
    CONFIG: "config",
    CEDEXIS: "cedexis",
    PRE_ROLL: "preroll",
    BMMR: "bmmr",
    LOTAME: "lotame",
    IMA_DAI_SDK: "imadaisdk",
    SPRITZ: "spritz"
};

const LOAD_STATUS = {
    COMPLETE: "COMPLETE",
    ERROR: "ERROR",
    INVALID_COMPLETE: "INVALID_COMPLETE",
    INVALID_ERROR: "INVALID_ERROR"
};

/**
 * @typedef {object} LoadTime
 * @property {string} url
 * @property {number} startTime
 * @property {number} startDeltaTime
 * @property {number} endDeltaTime
 * @property {number} endTime
 * @property {number} duration
 * @property {string} loadStatus
 */

/**
 * @type {LoadTime[]}
 */
let loadTimes = {};
let initTime = {};

let enabledByPlayer = {};

/**
 * @class LoadTimer
 */
class LoadTimer {
    /**
     * @static
     * @param {string} playerId
     * @param {boolean} enabled
     */
    static setEnabled(playerId, isEnabled) {
        enabledByPlayer[playerId] = isEnabled;
    }

    /**
     * @static
     * @param {string} playerId
     * @returns {boolean}
     */
    static getEnabled(playerId) {
        return !!enabledByPlayer[playerId];
    }

    /**
     * @static
     * @param {string} playerId
     * @param {string} loadType
     * @param {string} url
     */
    static trackLoadStart(playerId, loadType, url) {
        if (!enabledByPlayer[playerId]) {
            return;
        }

        const startTime = Date.now();
        if (loadType === LOAD_TYPES.PLAYER_INIT) {
            if (loadTimes[playerId]) {
                ErrorReport.recordGenericError(log, `Attempting to track a second load for ${ playerId } after a load for this player has already been tracked`);
            }

            loadTimes[playerId] = {};
            initTime[playerId] = startTime;
        }

        if (!loadTimes[playerId]) {
            ErrorReport.recordGenericError(log, `Attempting to track a ${ loadType } start for ${ playerId } before an init time has been set`);
            loadTimes[playerId] = {};
            initTime[playerId] = startTime;
        }

        if (loadTimes[playerId][loadType]) {
            const errorMessage = loadTimes[playerId][loadType].loadStatus
                ? `Attempting to track load start for ${ loadType } on ${ playerId } after the load has completed`
                : `Attempting to track load start for ${ loadType } on ${ playerId } after a load start exists`;
            ErrorReport.recordGenericError(log, errorMessage);
            return;
        }

        loadTimes[playerId][loadType] = {
            startDeltaTime: startTime - initTime[playerId],
            startTime,
            url
        };
    }

    /**
     * @static
     * @param {string} playerId
     * @param {string} loadType
     * @param {string} loadStatus
     */
    static trackLoadComplete(playerId, loadType, loadStatus) {
        if (!enabledByPlayer[playerId]) {
            return;
        }

        if (!loadTimes[playerId] || !loadTimes[playerId][loadType]) {
            ErrorReport.recordGenericError(log, `Attempting to complete ${ loadType } on ${ playerId } which has not been started`);

            if (!loadTimes[playerId]) {
                loadTimes[playerId] = {};
            }

            loadTimes[playerId][loadType] = {
                startDeltaTime: 0,
                startTime: initTime[playerId],
                url: "unknown"
            };

            // signal the invalid nature of this load time
            loadStatus = loadStatus == LOAD_STATUS.COMPLETE
                ? LOAD_STATUS.INVALID_COMPLETE
                : LOAD_STATUS.INVALID_ERROR;
        }

        if (loadTimes[playerId][loadType].loadStatus) {
            ErrorReport.recordGenericError(log, `Attempting to complete ${ loadType } on ${ playerId } which has already been completed`);
        }

        const endTime = Date.now();
        Object.assign(loadTimes[playerId][loadType], {
            endDeltaTime: endTime - initTime[playerId],
            duration: endTime - loadTimes[playerId][loadType].startTime,
            endTime,
            loadStatus
        });
    }

    /**
     * @static
     * @param {string} playerId
     * @returns {LoadTime[]}
     */
    static getLoadTimes(playerId) {
        return loadTimes[playerId];
    }
}

Object.defineProperties(LoadTimer, {
    "loadTypes": {
        value: LOAD_TYPES
    },
    "loadStatus": {
        value: LOAD_STATUS
    }
});

export default LoadTimer;

// FOR TESTING
function reset() {
    loadTimes = {};
    initTime = {};
    enabledByPlayer = {};
}

function getInitTime(playerId) {
    return initTime[playerId];
}

export { reset, getInitTime };
